import { Injectable } from '@angular/core';
import { DataProxy } from '@apollo/client/core';
import {
  Grant,
  GrantCourseViaSubscriptionInput,
  GrantCourseViaSubscriptionMutation,
  LoadCoursePromptQuery,
  LoadLearnerCourseListDocument,
  LoadLearnerCourseListQuery,
  LoadLearnerCourseListQueryResult,
  LoadLearnerCourseListQueryVariables,
  ProvideSupportGrantsDocument,
  ProvideSupportGrantsQueryResult,
  ProvideSupportGrantsQueryVariables,
  StartCourseInput,
  StartCourseMutation,
  UpdateLearnerVerifiedSessionInput,
  UpdateLearnerVerifiedSessionMutation
} from '@queries/generated';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class GrantsService {
  grants$ = this.loadLearnerCourseListQuery.watch({ includeArchived: false });

  constructor(
    private loadLearnerCourseListQuery: LoadLearnerCourseListQuery,
    private loadCoursePromptQuery: LoadCoursePromptQuery,
    private startCourseMutation: StartCourseMutation,
    private updateLearnerVerifiedSessionMutation: UpdateLearnerVerifiedSessionMutation,
    private grantCourseViaSubscriptionMutation: GrantCourseViaSubscriptionMutation
  ) {}

  loadList = (includeArchived: boolean = false) => {
    this.grants$.refetch({ includeArchived });
    return this.grants$.valueChanges.pipe(map(({ data }) => data.viewer.grants));
  };

  refresh() {
    this.grants$.refetch();
  }

  loadCoursePrompt = (grantId: number) => {
    return this.loadCoursePromptQuery
      .watch({ grantId })
      .valueChanges.pipe(map(({ data }) => data.grants.findById.course.coursePrompt));
  };

  startCourse(input: StartCourseInput, url: string) {
    return this.startCourseMutation.mutate({ input, url }).pipe(map(({ data }) => data.startCourse.grant));
  }

  updateLearnerVerifiedSession(input: UpdateLearnerVerifiedSessionInput) {
    return this.updateLearnerVerifiedSessionMutation
      .mutate({ input })
      .pipe(map(({ data }) => data.updateLearnerVerifiedSession.session));
  }

  grantCourseViaSubscription(input: GrantCourseViaSubscriptionInput) {
    return this.grantCourseViaSubscriptionMutation
      .mutate(
        { input },
        {
          update: (store, { data: { grantCourseViaSubscription } }) => {
            try {
              const { viewer } = store.readQuery<LoadLearnerCourseListQueryResult, LoadLearnerCourseListQueryVariables>(
                {
                  query: LoadLearnerCourseListDocument,
                  variables: { includeArchived: false }
                }
              );
              const data = {
                viewer: {
                  ...viewer,
                  grants: [...viewer.grants, grantCourseViaSubscription.grant]
                }
              };
              store.writeQuery<LoadLearnerCourseListQueryResult, LoadLearnerCourseListQueryVariables>({
                data,
                query: LoadLearnerCourseListDocument,
                variables: { includeArchived: false }
              });
            } catch (error) {
              console.warn('No course list cache to update');
            }
          }
        }
      )
      .pipe(map(({ data }) => data.grantCourseViaSubscription.grant));
  }

  removeFromCache(store: DataProxy, grant: Grant, supportedUserId: number) {
    const variables = {
      id: supportedUserId
    };

    const { provideSupport } = store.readQuery<ProvideSupportGrantsQueryResult, ProvideSupportGrantsQueryVariables>({
      query: ProvideSupportGrantsDocument,
      variables
    });
    const data = {
      provideSupport: {
        ...provideSupport,
        grants: {
          ...provideSupport.grants,
          nodes: provideSupport.grants.nodes.filter((g) => g.gid !== grant.gid)
        }
      }
    };
    store.writeQuery<ProvideSupportGrantsQueryResult, ProvideSupportGrantsQueryVariables>({
      query: ProvideSupportGrantsDocument,
      variables,
      data
    });
  }
}
